import { useState, useEffect } from "react";
import { Stepper } from "react-form-stepper";
import Toast from "react-bootstrap/Toast";
import ToastContainer from "react-bootstrap/ToastContainer";
import { useBlocker } from "react-router-dom";

// components
import Footer from "./../components/Footer";
import Navbar from "../components/SpectraNavbar";

// pages for Wizard
import WizardDeviceSelection from "../components/WizardPages/WizardDeviceSelection";
import WizardCalibrate from "../components/WizardPages/WizardCalibrate";
import WizardSampleInfo from "../components/WizardPages/WizardSampleInfo";
import WizardBulk from "../components/WizardPages/WizardBulk";
import WasatchWizardTrace from "../components/WizardPages/WasatchWizardTrace";
import WasatchWizardSummary from "../components/WizardPages/WasatchWizardSummary";

// contexts
import { useSampleContext, SampleProvider } from "../contexts";

import axios from "axios";

axios.defaults.baseURL = process.env.REACT_APP_API_URL;
axios.defaults.withCredentials = true;

const NewSampleInner = () => {
  // step tracker, this state is used to show corresponding wizard page
  const [step, setStep] = useState(0);
  const [stepAction, setStepAction] = useState("");

  //booleans for if calibration is needed and to show the successfull calibration toast
  const [isCalibrate, setIsCalibrate] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [submitStatus] = useState(true);
  const [sampleSubmitted, setSampleSubmitted] = useState(false);

  const { wasatchSample, resetSampleData } = useSampleContext();

  // Check if user has started entering sample data
  // They've started if they're past sample info (step > 1) or have entered any sample info
  const hasStartedEntry =
    step > 1 ||
    wasatchSample.presumed ||
    wasatchSample.colour?.length > 0 ||
    wasatchSample.texture?.id;

  // Reset sample data when component unmounts (user navigates away)
  useEffect(() => {
    return () => {
      resetSampleData();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty array ensures this only sets up the cleanup once

  // Block navigation within React Router if data entry has started but not submitted
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasStartedEntry &&
      !sampleSubmitted &&
      currentLocation.pathname !== nextLocation.pathname
  );

  // Handle browser/tab close or refresh
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasStartedEntry && !sampleSubmitted) {
        e.preventDefault();
        e.returnValue = ""; // Chrome requires returnValue to be set
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasStartedEntry, sampleSubmitted]);

  const wizardPages = {
    WizardDeviceSelection: 0,
    WizardSampleInfo: 1,
    WizardBulk: 2,
    WasatchWizardTrace: 3,
    WasatchWizardSummary: 4,
    WizardCalibrate: "calibrate",
  };

  // Labels for stepper, currently set to no labels by providing empty strings
  let wizardSteps = [];
  for (let i = 0; i < Object.keys(wizardPages).length - 1; i++) {
    wizardSteps.push({ label: "" });
  }

  // function to update the step
  // this will be passed into each Wizard Component
  // and will appropriately udpate the current step
  const changeStep = (value) => {
    setStepAction(value);

    if (value === "calibrate") {
      // setStep("calibrate");
      setIsCalibrate(true);
    }
    if (value === "return") {
      setStep(wizardPages.WizardDeviceSelection);
    }
    if (value === "continue") {
      setStep(wizardPages.WizardSampleInfo);
    }
    if (value === "forward" && step < 10) {
      setStep(step + 1);
    }
    if (value === "back" && step > 0) {
      setStep(step - 1);
    }
    if (value === "reset") {
      resetForm();
    }
  };

  const resetForm = () => {
    setStep(0);
    setSampleSubmitted(false); // Reset the submitted flag when form is reset
  };

  return (
    <div className='flex-container'>
      <Navbar authenticated={false} />

      {/* Navigation blocker confirmation dialog */}
      {blocker.state === "blocked" && (
        <div
          className='modal show d-block'
          tabIndex='-1'
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div
            className='modal-dialog modal-dialog-centered'
            role='dialog'
            aria-modal='true'
            aria-labelledby='modalTitle'
          >
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title' id='modalTitle'>
                  Unsaved Changes
                </h5>
              </div>
              <div className='modal-body'>
                <p>
                  You have unsaved sample data. Are you sure you want to leave
                  this page? Your progress will be lost.
                </p>
              </div>
              <div className='modal-footer'>
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={() => blocker.reset()}
                >
                  Stay on Page
                </button>
                <button
                  type='button'
                  className='btn btn-danger'
                  onClick={() => blocker.proceed()}
                >
                  Leave Page
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div aria-live='polite' aria-atomic='true' className='position-relative'>
        <ToastContainer
          position='top-end'
          style={{ zIndex: 1, marginTop: "79px" }}
        >
          <Toast
            onClose={() => setShowToast(false)}
            show={showToast}
            delay={5000}
            autohide
            bg={"success"}
            animation={true}
            className='d-inline-block'
          >
            <Toast.Header style={{ justifyContent: "space-between" }}>
              <strong>Calibration Successful!</strong>
            </Toast.Header>
            <Toast.Body style={{ color: "var(--bs-light)" }}>
              Device operating correctly and ready to scan.
            </Toast.Body>
          </Toast>
        </ToastContainer>
      </div>
      <div
        className='flex-body w-100 mx-auto py-3 px-sm-3 px-lg-5'
        style={{ marginTop: "79px" }}
      >
        {step !== "" && (
          <div className=''>
            <Stepper
              steps={wizardSteps}
              activeStep={step}
              // connectorStyleConfig={{stepSize:'3em'}} doesn't seem to be working atm
              styleConfig={{
                size: "1.5em",
                activeBgColor: "#1CA4EE",
                completedBgColor: "#8ED2F7",
                shape: "circle",
                color: "#fff",
              }}
              style={{ padding: "5px" }}
            />
          </div>
        )}
        <div className='mt-3 justify-content-center w-100 h-100 flex-grow-1 full-height'>
          <WizardDeviceSelection
            step={step}
            steps={wizardSteps}
            isCalibrate={isCalibrate}
            changeStep={changeStep}
            pageID={wizardPages.WizardDeviceSelection}
          />
          <WizardCalibrate
            step={step}
            isCalibrate={isCalibrate}
            setIsCalibrate={setIsCalibrate}
            steps={wizardSteps}
            changeStep={changeStep}
            pageID={wizardPages.WizardCalibrate}
            setShowToast={setShowToast}
          />
          <WizardSampleInfo
            step={step}
            steps={wizardSteps}
            changeStep={changeStep}
            pageID={wizardPages.WizardSampleInfo}
          />
          <WizardBulk
            step={step}
            steps={wizardSteps}
            changeStep={changeStep}
            pageID={wizardPages.WizardBulk}
          />
          <WasatchWizardTrace
            step={step}
            steps={wizardSteps}
            stepAction={stepAction}
            changeStep={changeStep}
            pageID={wizardPages.WasatchWizardTrace}
          />
          <WasatchWizardSummary
            step={step}
            steps={wizardSteps}
            pageID={wizardPages.WasatchWizardSummary}
            changeStep={changeStep}
            setSampleSubmitted={setSampleSubmitted}
          />
        </div>
        {!submitStatus && (
          <div className='alert alert-danger' role='alert'>
            Something went wrong, please try again!
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

const NewSample = () => (
  <SampleProvider>
    <NewSampleInner />
  </SampleProvider>
);

export default NewSample;
